/* The part of the week that is not work.
 *
 * This was called Balance and was built around a weekly budget: minutes
 * claimed, minutes measured, minutes missed. That framing is right for exactly
 * one of the three ways people actually have a hobby, and wrong in a way that
 * made the whole screen read like an accountant:
 *
 *   · **Things you keep up** — the gym, practice, physio. The request is not
 *     "four hours a week", it is "three times a week", and Amon holds those
 *     sessions before the week is offered to work.
 *   · **Things you would like to do** — a game, a book, an exhibition. Once,
 *     not weekly. Offered a slot when there is one, then finished.
 *   · **People** — seeing friends and family, which behaves like the first and
 *     reads nothing like it. Its own category, because "you have not seen
 *     anybody in a fortnight" is a sentence worth being able to say, and
 *     "3.5 hours under target" is not.
 *
 * And the points are gone. There was a currency you earned by living and spent
 * on rewards you had defined yourself — a game laid over a calendar that was
 * already telling the truth underneath. What replaced it is that truth: the
 * hours in the next seven days that are genuinely free, offered as hours, so
 * that "there was no time" becomes something you can check.
 */

import Section from '../ui/Section';
import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BalanceReading, Hobby, HobbySuggestion } from '@shared/types';
import { get, errorMessage } from '../api';
import { useSession, localToday } from '../session';
import HobbyList from './hobbies/HobbyList';
import Possible from './hobbies/Possible';
import FreeTime from './hobbies/FreeTime';
import { hours } from '../ui/time';

export default function HobbiesScreen() {
  const { today, user } = useSession();
  const qc = useQueryClient();
  const date = today ?? localToday(user?.tz);

  const hobbies = useQuery({
    queryKey: ['hobbies'],
    queryFn: async () => {
      const r = await get<{ hobbies: Hobby[]; suggested: HobbySuggestion[] }>('/api/hobbies');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your hobbies.'));
      return r;
    }
  });

  /* Still read, because it is the honest measurement of what actually
     happened — and it is the same number the hobby rows show, rather than a
     second one that could disagree. */
  const balance = useQuery({
    queryKey: ['balance', date],
    queryFn: async () => {
      const r = await get<BalanceReading>(`/api/balance?date=${date}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your week.'));
      return r;
    }
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['hobbies'] });
    void qc.invalidateQueries({ queryKey: ['balance'] });
    void qc.invalidateQueries({ queryKey: ['activity-types'] });
    void qc.invalidateQueries({ queryKey: ['free'] });
  };

  const list = hobbies.data?.hobbies ?? [];
  const keep = useMemo(() => list.filter(h => h.category !== 'possible' && h.category !== 'social'), [list]);
  const people = useMemo(() => list.filter(h => h.category === 'social'), [list]);

  const doneThisWeek = list.reduce((a, h) => a + (h.doneMin ?? 0), 0);

  return (
    <div className="screen">
      <header className="topbar">
        <div>
          <h1>Hobbies</h1>
          <p className="muted">
            {doneThisWeek > 0
              ? `${hours(doneThisWeek)} of your own so far this week`
              : 'the part of the week that is not work'}
          </p>
        </div>
      </header>

      {hobbies.isError && (
        <div className="notice notice-bad" role="alert">
          <p><strong>Could not read your hobbies.</strong></p>
          <p className="muted small">{(hobbies.error as Error).message}</p>
          <button className="btn btn-ghost btn-sm" onClick={() => hobbies.refetch()}>Try again</button>
        </div>
      )}

      <HobbyList
        title="Things you keep up"
        blurb="The gym, practice, the instrument. Say how many times a week rather than how
               many hours — three sessions of forty minutes is a different request from two
               hours whenever, and only the first is what anybody means by training three
               times a week."
        category="must"
        hobbies={keep}
        suggested={hobbies.data?.suggested ?? []}
        onChanged={refresh} />

      <HobbyList
        title="People"
        blurb="Seeing friends and family is the first thing a full week quietly deletes, and
               the last thing anybody puts in a planner. Claim it the same way, and Amon
               holds it the same way."
        category="social"
        hobbies={people}
        suggested={[{ name: 'Friends', color: 'blue', minutesPerWeek: 180 },
          { name: 'Family', color: 'rose', minutesPerWeek: 180 }]}
        onChanged={refresh} />

      <Possible hobbies={list} today={date} />

      <FreeTime today={date} hobbies={list} />

      {balance.data?.patterns && balance.data.patterns.length > 0 && (
        <Section id="patterns-title" icon="flag" className="tone-slate"
                 title="What keeps losing" meta="measured, not guessed">
          {balance.data.patterns.map(p => (
            <p key={p.ritual} className="muted small">{p.sentence}</p>
          ))}
        </Section>
      )}
    </div>
  );
}
